import { CheckCircle2, Save, Sparkles, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AdminApi } from "@/api";
import { Badge, Button, Disclosure, SelectField, TextField, TextareaField } from "@/components/ui";
import type { SubAgentProfile, SubAgentProfileVersion, SubAgentProfileVersionList } from "@/types";
import { labelForSubAgent } from "@/utils/prompts";
import { diffLines, hasChanges } from "@/utils/textDiff";
import { TextDiffView } from "./TextDiffView";

function parseNames(value: string) {
  return [...new Set(value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean))];
}

type Draft = {
  promptBody: string;
  toolGuidelineBody: string;
  toolNames: string;
  skillNames: string;
  model: string;
  maxRounds: string;
};

function draftOf(version: SubAgentProfileVersion | null): Draft {
  return {
    promptBody: version?.prompt_body ?? "",
    toolGuidelineBody: version?.tool_guideline_body ?? "",
    toolNames: (version?.tool_names ?? []).join("\n"),
    skillNames: (version?.skill_names ?? []).join("\n"),
    model: version?.model ?? "",
    maxRounds: String(version?.max_rounds ?? 8),
  };
}

/**
 * Versions of one sub-agent profile (role prompt + execution settings). Save
 * adds an inactive version; Apply makes it the one that runs — for the
 * orchestrator that is the main chat's tool and skill surface.
 */
export function SubAgentVersionsPanel({
  api,
  role,
  onFlash,
  onError,
}: {
  api: AdminApi;
  role: SubAgentProfile["role"];
  onFlash: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [list, setList] = useState<SubAgentProfileVersionList | null>(null);
  const [baseId, setBaseId] = useState("");
  const [draft, setDraft] = useState<Draft>(draftOf(null));
  const [busy, setBusy] = useState(false);

  const versions = list?.versions ?? [];
  const active = versions.find((version) => version.status === "active") ?? null;
  const base = versions.find((version) => version.id === baseId) ?? null;
  const label = labelForSubAgent(role);

  const loadBase = useCallback((version: SubAgentProfileVersion | null) => {
    setBaseId(version?.id ?? "");
    setDraft(draftOf(version));
  }, []);

  const reload = useCallback(
    async (keepEditor = true) => {
      const next = await api.listSubAgentProfileVersions(role);
      setList(next);
      if (!keepEditor) {
        loadBase(next.versions.find((version) => version.id === next.latest_version_id) ?? null);
      }
      return next;
    },
    [api, role, loadBase],
  );

  useEffect(() => {
    reload(false).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const baseDraft = draftOf(base);
  const diffs = useMemo(
    () =>
      (
        [
          ["Role prompt", "promptBody"],
          ["Tool guideline", "toolGuidelineBody"],
          ["Allowed tools", "toolNames"],
          ["Visible skills", "skillNames"],
          ["Model", "model"],
          ["Maximum rounds", "maxRounds"],
        ] as const
      ).map(([title, key]) => ({ title, lines: diffLines(baseDraft[key], draft[key]) })),
    [baseDraft, draft],
  );
  const dirty = diffs.some((diff) => hasChanges(diff.lines));

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      const saved = await api.saveSubAgentProfileVersion(role, {
        prompt_body: draft.promptBody,
        tool_guideline_body: draft.toolGuidelineBody || null,
        tool_names: parseNames(draft.toolNames),
        skill_names: parseNames(draft.skillNames),
        model: draft.model || null,
        max_rounds: Math.min(25, Math.max(1, Number.parseInt(draft.maxRounds, 10) || 1)),
        base_version_id: baseId || null,
      });
      const next = await reload();
      loadBase(next.versions.find((version) => version.id === saved.id) ?? null);
      onFlash(`Saved ${label} v${saved.version}. It is not applied yet.`);
    });

  const apply = (version: SubAgentProfileVersion) => {
    const scope = role === "orchestrator" ? " This changes the main chat's tools and skills." : "";
    if (!window.confirm(`Apply ${label} v${version.version}?${scope}`)) return;
    return run(async () => {
      setList(await api.activateSubAgentProfileVersion(role, version.id));
      onFlash(`${label} v${version.version} is now serving.`);
    });
  };

  const remove = (version: SubAgentProfileVersion) => {
    if (!window.confirm(`Delete ${label} v${version.version}? It is hidden, not erased.`)) return;
    return run(async () => {
      setList(await api.deleteSubAgentProfileVersion(role, version.id));
      if (version.id === baseId) loadBase(null);
      onFlash(`Deleted ${label} v${version.version}.`);
    });
  };

  const set = (key: keyof Draft) => (value: string) => setDraft({ ...draft, [key]: value });

  return (
    <Disclosure
      title={`${label} · ${active ? `serving v${active.version}` : "nothing serving"}`}
      subtitle={
        active
          ? `${active.tool_names.length} tools · ${active.skill_names.length} skills · max ${active.max_rounds} rounds`
          : undefined
      }
      defaultOpen={false}
    >
      <div className="grid gap-4">
        <div className="grid gap-2">
          {versions.map((version) => (
            <div
              key={version.id}
              className={`grid gap-1.5 rounded-lg border p-3 ${version.id === baseId ? "border-ring" : "border-border"}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <strong>v{version.version}</strong>
                {version.status === "active" && (
                  <Badge size="compact" variant="accent">
                    serving
                  </Badge>
                )}
                {list?.latest_version_id === version.id && <Badge size="compact">latest</Badge>}
                <small className="text-xs text-muted-foreground">
                  prompt v{version.prompt_version} · {version.tool_names.length} tools · max{" "}
                  {version.max_rounds} rounds
                </small>
                <span className="ml-auto flex flex-wrap gap-1.5">
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => loadBase(version)}>
                    Edit from
                  </Button>
                  {version.status !== "active" && (
                    <>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => apply(version)}>
                        <CheckCircle2 /> Apply
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(version)}>
                        <Trash2 /> Delete
                      </Button>
                    </>
                  )}
                </span>
              </div>
              {version.change_summary && (
                <div className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 size-3.75 shrink-0 text-muted-foreground" />
                  <p className="m-0 text-sm whitespace-pre-wrap text-muted-foreground">
                    Prompt v{version.prompt_version}: {version.change_summary}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid gap-3 rounded-lg border border-border p-3.5">
          <SelectField
            label="Edit from (base version)"
            value={baseId}
            onChange={(id) => loadBase(versions.find((version) => version.id === id) ?? null)}
            options={[
              { value: "", label: "(empty — first version)" },
              ...versions.map((version) => ({
                value: version.id,
                label: `v${version.version}${version.status === "active" ? " (serving)" : ""}${
                  list?.latest_version_id === version.id ? " (latest)" : ""
                }`,
              })),
            ]}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Model override" value={draft.model} onChange={set("model")} placeholder="Blank uses the server default" />
            <TextField label="Maximum rounds" value={draft.maxRounds} onChange={set("maxRounds")} type="number" />
          </div>
          <TextareaField label="Role prompt" value={draft.promptBody} onChange={set("promptBody")} rows={12} mono />
          <TextareaField label="Tool guideline" value={draft.toolGuidelineBody} onChange={set("toolGuidelineBody")} rows={10} mono />
          <div className="grid grid-cols-2 gap-3">
            <TextareaField label="Allowed tools (one per line)" value={draft.toolNames} onChange={set("toolNames")} rows={10} mono />
            <TextareaField label="Visible skills (one per line)" value={draft.skillNames} onChange={set("skillNames")} rows={10} mono />
          </div>
          {dirty && (
            <Disclosure title={`Changes against ${base ? `v${base.version}` : "empty"}`} defaultOpen={false}>
              {diffs
                .filter((diff) => hasChanges(diff.lines))
                .map((diff) => (
                  <TextDiffView key={diff.title} title={diff.title} lines={diff.lines} />
                ))}
            </Disclosure>
          )}
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || !dirty || !draft.promptBody.trim()} onClick={save}>
              <Save /> Save as new version
            </Button>
          </div>
          <p className="m-0 text-xs text-muted-foreground">
            Saving never changes what runs. Use Apply on a version to put it live.
          </p>
        </div>
      </div>
    </Disclosure>
  );
}
